import { useState, useEffect } from 'react';
import { X, Loader2, Search, User } from 'lucide-react';
import { tasksApi } from '@/api/endpoints/tasks.api';
import { usersApi } from '@/api/endpoints/users.api';
import { patientsApi } from '@/api/endpoints/patients.api';
import { maskCPF } from '@/lib/utils';
import {
  TaskType,
  TaskTypeLabels,
  TaskPriority,
  TaskPriorityLabels,
  RecurrencePattern,
  RecurrencePatternLabels,
  DayOfWeekLabels,
} from '@/types/task.types';

interface CreateTaskDialogProps {
  patientId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface PatientOption {
  id: string;
  name: string;
  cpf: string;
}

export default function CreateTaskDialog({
  patientId: initialPatientId,
  onClose,
  onSuccess,
}: CreateTaskDialogProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: '' as TaskType | '',
    priority: 'MEDIUM' as TaskPriority,
    dueDate: '',
    patientId: initialPatientId || '',
    assignedToId: '',
    // Recurrence
    isRecurring: false,
    recurrencePattern: '' as RecurrencePattern | '',
    recurrenceInterval: 1,
    recurrenceDayOfWeek: '' as number | '',
    recurrenceDayOfMonth: '' as number | '',
    recurrenceEndDate: '',
    recurrenceCount: '' as number | '',
  });

  const [users, setUsers] = useState<UserOption[]>([]);
  const [patients, setPatients] = useState<PatientOption[]>([]);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientSearch, setShowPatientSearch] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await usersApi.getAll({ page: 0, size: 100 });
        setUsers(
          response.content
            .filter((u) => u.active)
            .map((u) => ({ ...u, role: u.roles[0] || '' }))
        );
      } catch (err) {
        console.error('Erro ao carregar usuários:', err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (initialPatientId) {
      const fetchPatient = async () => {
        try {
          const patient = await patientsApi.getById(initialPatientId);
          setSelectedPatient({ id: patient.id, name: patient.name, cpf: patient.cpf });
        } catch (err) {
          console.error('Erro ao carregar paciente:', err);
        }
      };
      fetchPatient();
    }
  }, [initialPatientId]);

  useEffect(() => {
    const searchPatients = async () => {
      if (patientSearch.length < 2) {
        setPatients([]);
        return;
      }
      try {
        const response = await patientsApi.getAll({ page: 0, size: 10, search: patientSearch });
        setPatients(response.content.map((p) => ({ id: p.id, name: p.name, cpf: p.cpf })));
      } catch (err) {
        console.error('Erro ao buscar pacientes:', err);
      }
    };

    const debounce = setTimeout(searchPatients, 300);
    return () => clearTimeout(debounce);
  }, [patientSearch]);

  const handleSubmit = async () => {
    if (!formData.type) {
      setError('Selecione o tipo da tarefa');
      return;
    }
    if (!formData.title.trim()) {
      setError('Digite o título da tarefa');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await tasksApi.create({
        title: formData.title.trim(),
        description: formData.description.trim() || undefined,
        type: formData.type as TaskType,
        priority: formData.priority,
        dueDate: formData.dueDate || undefined,
        patientId: formData.patientId || undefined,
        assignedToId: formData.assignedToId || undefined,
        // Recurrence
        isRecurring: formData.isRecurring,
        recurrencePattern: formData.isRecurring && formData.recurrencePattern ? formData.recurrencePattern as RecurrencePattern : undefined,
        recurrenceInterval: formData.isRecurring && formData.recurrenceInterval ? formData.recurrenceInterval : undefined,
        recurrenceDayOfWeek: formData.isRecurring && formData.recurrenceDayOfWeek ? Number(formData.recurrenceDayOfWeek) : undefined,
        recurrenceDayOfMonth: formData.isRecurring && formData.recurrenceDayOfMonth ? Number(formData.recurrenceDayOfMonth) : undefined,
        recurrenceEndDate: formData.isRecurring && formData.recurrenceEndDate ? formData.recurrenceEndDate : undefined,
        recurrenceCount: formData.isRecurring && formData.recurrenceCount ? Number(formData.recurrenceCount) : undefined,
      });
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Erro ao criar tarefa:', err);
      setError('Erro ao criar tarefa. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectPatient = (patient: PatientOption) => {
    setSelectedPatient(patient);
    setFormData({ ...formData, patientId: patient.id });
    setShowPatientSearch(false);
    setPatientSearch('');
  };

  const clearPatient = () => {
    setSelectedPatient(null);
    setFormData({ ...formData, patientId: '' });
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      ADMIN: 'Administrador',
      DOCTOR: 'Médico',
      NURSE: 'Enfermeiro',
      RECEPTIONIST: 'Recepcionista',
    };
    return labels[role] || role;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-background p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Nova Tarefa</h2>
          <button onClick={onClose} className="rounded p-1 hover:bg-muted">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {/* Title */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Título <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Título da tarefa"
              className="w-full rounded-lg border bg-background p-3"
              maxLength={255}
            />
          </div>

          {/* Type */}
          <div>
            <label className="mb-1 block text-sm font-medium">
              Tipo <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as TaskType })}
              className="w-full rounded-lg border bg-background p-3"
            >
              <option value="">Selecione o tipo</option>
              {Object.entries(TaskTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div>
            <label className="mb-1 block text-sm font-medium">Prioridade</label>
            <div className="flex gap-2">
              {Object.entries(TaskPriorityLabels).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setFormData({ ...formData, priority: value as TaskPriority })}
                  className={`flex-1 rounded-lg border px-4 py-2 text-sm transition-colors ${
                    formData.priority === value
                      ? value === 'URGENT'
                        ? 'border-red-300 bg-red-50 text-red-700'
                        : value === 'HIGH'
                          ? 'border-orange-300 bg-orange-50 text-orange-700'
                          : value === 'MEDIUM'
                            ? 'border-yellow-300 bg-yellow-50 text-yellow-700'
                            : 'border-green-300 bg-green-50 text-green-700'
                      : 'hover:bg-muted'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Due Date */}
          <div>
            <label className="mb-1 block text-sm font-medium">Prazo</label>
            <input
              type="date"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              className="w-full rounded-lg border bg-background p-3"
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Description */}
          <div>
            <label className="mb-1 block text-sm font-medium">Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Descreva os detalhes da tarefa..."
              rows={3}
              className="w-full rounded-lg border bg-background p-3"
            />
          </div>

          {/* Patient */}
          <div>
            <label className="mb-1 block text-sm font-medium">Paciente (opcional)</label>
            {selectedPatient ? (
              <div className="flex items-center justify-between rounded-lg border bg-muted/50 p-3">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{selectedPatient.name}</p>
                    <p className="text-sm text-muted-foreground">CPF: {maskCPF(selectedPatient.cpf)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearPatient}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    value={patientSearch}
                    onChange={(e) => {
                      setPatientSearch(e.target.value);
                      setShowPatientSearch(true);
                    }}
                    onFocus={() => setShowPatientSearch(true)}
                    placeholder="Buscar paciente por nome ou CPF..."
                    className="w-full rounded-lg border bg-background py-3 pl-10 pr-4"
                  />
                </div>
                {showPatientSearch && patients.length > 0 && (
                  <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border bg-background shadow-lg">
                    {patients.map((patient) => (
                      <button
                        key={patient.id}
                        type="button"
                        onClick={() => selectPatient(patient)}
                        className="flex w-full items-center gap-2 p-3 text-left hover:bg-muted"
                      >
                        <User className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{patient.name}</p>
                          <p className="text-sm text-muted-foreground">CPF: {maskCPF(patient.cpf)}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Assigned To */}
          <div>
            <label className="mb-1 block text-sm font-medium">Responsável (opcional)</label>
            <select
              value={formData.assignedToId}
              onChange={(e) => setFormData({ ...formData, assignedToId: e.target.value })}
              className="w-full rounded-lg border bg-background p-3"
            >
              <option value="">Não atribuída</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name} ({getRoleLabel(user.role)})
                </option>
              ))}
            </select>
          </div>

          {/* Recurrence */}
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isRecurring"
                checked={formData.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="isRecurring" className="text-sm font-medium">
                Tarefa recorrente
              </label>
            </div>

            {formData.isRecurring && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                {/* Recurrence Pattern */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Frequência</label>
                  <select
                    value={formData.recurrencePattern}
                    onChange={(e) => setFormData({ ...formData, recurrencePattern: e.target.value as RecurrencePattern })}
                    className="w-full rounded-lg border bg-background p-2 text-sm"
                  >
                    <option value="">Selecione</option>
                    {Object.entries(RecurrencePatternLabels).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* Recurrence Interval */}
                <div>
                  <label className="mb-1 block text-sm font-medium">A cada</label>
                  <input
                    type="number"
                    min={1}
                    max={99}
                    value={formData.recurrenceInterval}
                    onChange={(e) => setFormData({ ...formData, recurrenceInterval: Number(e.target.value) })}
                    className="w-full rounded-lg border bg-background p-2 text-sm"
                  />
                </div>

                {/* Day of Week (for weekly) */}
                {formData.recurrencePattern === 'WEEKLY' && (
                  <div>
                    <label className="mb-1 block text-sm font-medium">Dia da semana</label>
                    <select
                      value={formData.recurrenceDayOfWeek}
                      onChange={(e) => setFormData({ ...formData, recurrenceDayOfWeek: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background p-2 text-sm"
                    >
                      <option value="">Qualquer dia</option>
                      {Object.entries(DayOfWeekLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Day of Month (for monthly) */}
                {formData.recurrencePattern === 'MONTHLY' && (
                  <div>
                    <label className="mb-1 block text-sm font-medium">Dia do mês</label>
                    <select
                      value={formData.recurrenceDayOfMonth}
                      onChange={(e) => setFormData({ ...formData, recurrenceDayOfMonth: Number(e.target.value) })}
                      className="w-full rounded-lg border bg-background p-2 text-sm"
                    >
                      <option value="">Mesmo dia</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                        <option key={day} value={day}>Dia {day}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* End Date */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Data final (opcional)</label>
                  <input
                    type="date"
                    value={formData.recurrenceEndDate}
                    onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value })}
                    className="w-full rounded-lg border bg-background p-2 text-sm"
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                {/* Max Occurrences */}
                <div>
                  <label className="mb-1 block text-sm font-medium">Máx. ocorrências (opcional)</label>
                  <input
                    type="number"
                    min={1}
                    max={999}
                    value={formData.recurrenceCount}
                    onChange={(e) => setFormData({ ...formData, recurrenceCount: e.target.value ? Number(e.target.value) : '' })}
                    placeholder="Sem limite"
                    className="w-full rounded-lg border bg-background p-2 text-sm"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={submitting}
            className="rounded-lg border px-4 py-2 hover:bg-muted disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Criar Tarefa
          </button>
        </div>
      </div>
    </div>
  );
}
